/**
 * Shared search → home-URL mapping. The AI search bar and the floating orb
 * both translate a /api/ai/search response into the SAME home-page query
 * params, so results land on "/" (the single events surface) in search mode.
 * Param names match the home loader / backend contract exactly:
 *   area/category = CSV · cost/time/when/q single · aiq/note/widened = banner.
 */

export interface SearchFilters {
  areas: string[];
  categories: string[];
  cost: 'free' | 'paid' | 'any';
  when: string | null;
  timeOfDay: 'morning' | 'afternoon' | 'evening' | null;
  keywords: string;
  interpretation: string;
}

export interface SearchResponse {
  events: unknown[];
  filters: SearchFilters;
  interpretation: string;
  aiUsed: boolean;
  widened?: boolean;
}

/** Build the home query string that reflects a structured AI search result. */
export function filtersToHomeQuery(
  filters: SearchFilters,
  rawQuery: string,
  interpretation: string,
  widened: boolean
): string {
  const params = new URLSearchParams();
  if (filters.areas.length) params.set('area', filters.areas.join(','));
  if (filters.categories.length) params.set('category', filters.categories.join(','));
  if (filters.cost && filters.cost !== 'any') params.set('cost', filters.cost);
  if (filters.timeOfDay) params.set('time', filters.timeOfDay);
  if (filters.keywords) params.set('q', filters.keywords);
  params.set('when', filters.when ?? 'all');
  params.set('aiq', rawQuery);
  if (interpretation) params.set('note', interpretation);
  if (widened) params.set('widened', '1');
  return params.toString();
}

/** Plain keyword fallback (AI unavailable) — search all upcoming on home. */
export function keywordHomeQuery(text: string): string {
  const params = new URLSearchParams();
  params.set('q', text);
  params.set('when', 'all');
  params.set('aiq', text);
  return params.toString();
}
